import { iceHints, type IceCameraView, type IcePlayback, type Square } from '@halvard/board3d';
import type { IceBotLevel } from '@halvard/ai';
import { ice } from '@halvard/rules';
import { createStore } from 'zustand/vanilla';
import { t } from '../../i18n';
import { describeIceEvents } from './describe';

/** Who plays a side: a person at this screen, a bot of some level, or an online opponent. */
export type IcePlayer = 'human' | 'remote' | IceBotLevel;
export type IcePlayers = Record<ice.Side, IcePlayer>;
export const TWO_HUMANS: IcePlayers = { brand: 'human', rime: 'human' };

/**
 * One played action: the state before it and what happened, for undo and the log. The log's
 * sentences are written when shown, so they follow a change of language.
 */
export interface IceTurn {
  before: ice.IceState;
  notation: string;
  side: ice.Side;
  events: readonly ice.IceEvent[];
}

export interface IceGameState {
  game: ice.IceState;
  history: IceTurn[];
  /** Warrior type the placement tray will place next. */
  placeType: ice.WarriorType;
  selectedId: string | null;
  /** An enemy picked as the shove target; its routes stay drawn while an approach is chosen. */
  target: Square | null;
  /** The square under the pointer or the keyboard cursor. */
  focus: Square | null;
  cursor: Square;
  view: IceCameraView;
  playback: IcePlayback | null;
  /** Latest sentence for screen readers. */
  announcement: string;
  players: IcePlayers;
  /** Names to show for each side, when known (online opponents). */
  names: Partial<Record<ice.Side, string>>;
  /** A bot is choosing its move. */
  thinking: boolean;
  /** Online: an action was sent and the server hasn't answered yet. */
  sending: boolean;
}

export interface IceGameActions {
  start: (seed: number, crackSeed: number) => void;
  activate: (square: Square) => void;
  select: (id: string | null) => void;
  setPlaceType: (type: ice.WarriorType) => void;
  setFocus: (square: Square | null) => void;
  setCursor: (square: Square) => void;
  setView: (view: IceCameraView) => void;
  pass: () => void;
  undo: () => void;
  cancel: () => void;
  /** A bot's move, played if it is still that bot's turn and the action is legal. */
  playBot: (action: ice.IceAction) => void;
  setThinking: (thinking: boolean) => void;
  /** Online: the server played an action (ours or the opponent's). */
  receive: (update: {
    state: ice.IceState;
    events: readonly ice.IceEvent[];
    side: ice.Side;
  }) => void;
  /** Online: a game from a snapshot, or the second game of a match. Clears the log. */
  load: (game: ice.IceState, players: IcePlayers, names: Partial<Record<ice.Side, string>>) => void;
  /** Online: the server refused our action. */
  rejected: (message: string) => void;
}

export interface IceGameStoreOptions {
  /** Online: actions go to the server instead of being played here. */
  submit?: (action: ice.IceAction) => void;
}

export type IceGameStore = IceGameState & IceGameActions;
export type IceGameStoreApi = ReturnType<typeof createIceGameStore>;

const same = (a: Square | null, b: Square | null) =>
  a !== null && b !== null && a.r === b.r && a.c === b.c;

/** The first warrior type the side to move still has to place, keeping `wanted` if it can. */
export function placeableType(game: ice.IceState, wanted: ice.WarriorType): ice.WarriorType {
  const left = ice.toPlace(game, game.turn);
  if (left[wanted] > 0) return wanted;
  return ice.WARRIOR_TYPES.find((t) => left[t] > 0) ?? wanted;
}

export function sideToMoveText(game: ice.IceState): string {
  const side = t().sides[game.turn];
  return game.phase === 'placing' ? t().game.places(side) : t().game.toMove(side);
}

export function isBotTurn(state: Pick<IceGameState, 'game' | 'players'>): boolean {
  return state.game.phase !== 'over' && state.players[state.game.turn] !== 'human';
}

export function createIceGameStore(
  seed: number,
  crackSeed: number,
  players = TWO_HUMANS,
  options: IceGameStoreOptions = {},
) {
  let playbackKey = 0;
  const fresh = (s: number, c: number): IceGameState => ({
    players,
    names: {},
    thinking: false,
    sending: false,
    game: ice.newIceGame({ seed: s, crackSeed: c }),
    history: [],
    placeType: 'huscarl',
    selectedId: null,
    target: null,
    focus: null,
    cursor: { r: 7, c: 4 },
    view: players.brand === 'human' || players.rime !== 'human' ? 'brand' : 'rime',
    playback: null,
    announcement: t().log.lakeStart(s),
  });

  return createStore<IceGameStore>()((set, get) => {
    /** Moves the game on to `state`, logging and animating what happened. */
    const advance = (state: ice.IceState, events: readonly ice.IceEvent[], side: ice.Side) => {
      const { game, history, placeType } = get();
      const lines = describeIceEvents(game, events);
      const next = state.phase === 'over' ? '' : ` ${sideToMoveText(state)}.`;
      set({
        game: state,
        history: [
          ...history,
          { before: game, notation: state.moves.at(-1) ?? '', side, events: [...events] },
        ],
        placeType: state.phase === 'placing' ? placeableType(state, placeType) : placeType,
        selectedId: null,
        target: null,
        sending: false,
        playback: { key: ++playbackKey, before: game, events: [...events] },
        announcement: `${lines.join(' ')}${next}`,
      });
    };

    const play = (action: ice.IceAction) => {
      if (options.submit) {
        if (get().sending) return;
        set({ sending: true });
        options.submit(action);
        return;
      }
      const { game } = get();
      const { state, events } = ice.applyIceAction(game, action);
      advance(state, events, game.turn);
    };

    const ownWarriorAt = (square: Square) => {
      const { game } = get();
      const w = ice.warriorAt(game, square.r, square.c);
      return w && w.side === game.turn ? w : undefined;
    };

    return {
      ...fresh(seed, crackSeed),

      start: (s, c) => set(fresh(s, c)),

      activate: (square) => {
        const { game, selectedId, target, placeType } = get();
        if (game.phase === 'over' || isBotTurn(get())) return;

        if (game.phase === 'placing') {
          const action: ice.IceAction = { kind: 'place', type: placeType, at: square };
          if (ice.legalIceActions(game).some((a) => ice.sameIceAction(a, action))) play(action);
          return;
        }

        // A target is picked: a click on one of its approach squares (or on the target itself,
        // for the highlighted route) plays the shove.
        if (target) {
          const focusOn = same(square, target) ? null : square;
          const hints = iceHints(game, selectedId, target, focusOn ?? get().focus);
          const route = focusOn
            ? hints.routes.find((r) => same(r.option.action.from, focusOn))
            : hints.routes.find((r) => r.emphasis === 'strong');
          if (route) {
            const { option } = route;
            if (option.legal) play(option.action);
            else {
              set({
                announcement: t().log.shoveFailsNow(option.strength, option.wall),
              });
            }
            return;
          }
          set({ target: null });
        }

        const own = ownWarriorAt(square);
        if (own) {
          set({ selectedId: own.id === selectedId ? null : own.id, target: null });
          return;
        }
        const selected = game.warriors.find((w) => w.id === selectedId);
        if (!selected) return;

        const enemy = ice.warriorAt(game, square.r, square.c);
        if (enemy) {
          const reachable = ice
            .shoveOptions(game, selected)
            .some((o) => same(o.action.target, square));
          if (reachable) {
            set({ target: square, announcement: t().log.chooseFrom });
          }
          return;
        }
        const move: ice.IceAction = { kind: 'move', id: selected.id, to: square };
        if (ice.legalIceActions(game).some((a) => ice.sameIceAction(a, move))) play(move);
        else set({ selectedId: null });
      },

      select: (id) => set({ selectedId: id, target: null }),
      setPlaceType: (type) => set({ placeType: placeableType(get().game, type) }),
      setFocus: (square) => {
        if (!same(square, get().focus) && (square !== null || get().focus !== null)) {
          set({ focus: square });
        }
      },
      setCursor: (square) => set({ cursor: square }),
      setView: (view) => set({ view }),

      pass: () => {
        if (isBotTurn(get())) return;
        const legal = ice.legalIceActions(get().game);
        if (legal.length === 1 && legal[0]?.kind === 'pass') play({ kind: 'pass' });
      },

      undo: () => {
        const { history, players } = get();
        // Online moves are final.
        if (players.brand === 'remote' || players.rime === 'remote') return;
        // Against a bot, step back to the last turn a person played.
        let keep = history.length - 1;
        while (keep >= 0 && players[history[keep]?.side ?? 'brand'] !== 'human') keep--;
        const last = history[keep];
        if (!last) return;
        set({
          game: last.before,
          history: history.slice(0, keep),
          thinking: false,
          selectedId: null,
          target: null,
          playback: null,
          announcement: t().log.undone(last.notation, sideToMoveText(last.before)),
        });
      },

      playBot: (action) => {
        if (!isBotTurn(get())) return;
        if (ice.legalIceActions(get().game).some((a) => ice.sameIceAction(a, action))) {
          play(action);
        }
      },

      setThinking: (thinking) => set({ thinking }),

      receive: ({ state, events, side }) => advance(state, events, side),

      load: (game, nextPlayers, names) =>
        set({
          game,
          players: nextPlayers,
          names,
          history: [],
          selectedId: null,
          target: null,
          sending: false,
          playback: null,
          placeType:
            game.phase === 'placing' ? placeableType(game, get().placeType) : get().placeType,
          view: nextPlayers.rime === 'human' && nextPlayers.brand !== 'human' ? 'rime' : 'brand',
          announcement: t().log.lakeNow(game.lake.seed, sideToMoveText(game)),
        }),

      rejected: (message) => set({ sending: false, announcement: message }),

      cancel: () => {
        const { target } = get();
        set(target ? { target: null } : { selectedId: null });
      },
    };
  });
}
